import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { usePasswordValidation } from '../hooks/usePasswordValidation';
import { passwordSchema } from '../schemas/password';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';

const setupFormSchema = passwordSchema.extend({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required')
});

type SetupFormValues = z.infer<typeof setupFormSchema>;

export type PasswordSetupData = {
  password: string;
  firstName: string;
  lastName: string;
};

interface PasswordSetupFormProps {
  onSubmit: (data: PasswordSetupData) => Promise<void>;
  loading: boolean;
  error?: string | null;
  showNameFields?: boolean;
}

export const PasswordSetupForm: React.FC<PasswordSetupFormProps> = ({
  onSubmit,
  loading,
  error,
  showNameFields = true
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<SetupFormValues>({
    resolver: zodResolver(setupFormSchema),
    defaultValues: {
      password: '',
      firstName: '',
      lastName: ''
    }
  });

  const passwordValue = form.watch('password');
  const { passwordStrength, showPasswordStrength } = usePasswordValidation(passwordValue);

  const handleSubmit = async (values: SetupFormValues) => {
    await onSubmit({
      password: values.password,
      firstName: values.firstName,
      lastName: values.lastName
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(handleSubmit)(e);
        }}
        className="flex flex-col gap-3.5"
      >
        {/* Name Fields */}
        {showNameFields && (
          <div className="flex flex-col gap-3.5 sm:flex-row">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-[13px] font-semibold text-[var(--ink)]">First name</FormLabel>
                  <FormControl>
                    <Input {...field} type="text" placeholder="Enter your first name" aria-label="First name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-[13px] font-semibold text-[var(--ink)]">Last name</FormLabel>
                  <FormControl>
                    <Input {...field} type="text" placeholder="Enter your last name" aria-label="Last name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Password */}
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-[13px] font-semibold text-[var(--ink)]">Password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Enter your password"
                    aria-label="Password"
                    aria-describedby="password-strength"
                    className="pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-[15px] top-1/2 -translate-y-1/2 text-[var(--ink-3)] transition hover:text-[var(--ink)]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Password Strength Indicator */}
        <PasswordStrengthIndicator strength={passwordStrength} showStrength={showPasswordStrength} />

        {/* Error Message Display */}
        {error && <div className="text-loss text-sm font-medium">{error}</div>}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !form.formState.isValid}
          className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Create account with password"
        >
          {loading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>Creating account...</span>
            </div>
          ) : (
            'Create Account'
          )}
        </button>
      </form>
    </Form>
  );
};
